import { Box, Button, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import AddPhotoAlternate from "@mui/icons-material/AddPhotoAlternate";
import AttachFile from "@mui/icons-material/AttachFile";
import { useRef, useState } from "react";
import { api } from "../../../api/API";
import { Attachment } from "../../../api/Tasks";
import { useImagePreview } from "../../../misc/HtmlImage";
import { useSnackAction } from "../../../misc/SnackAction";
import { useSyncedRef } from "../../../misc/SyncedRef";
import { gutter } from "./MobileSheet";

type Uploading = {
    key: string;

    // url shows the picked image while its upload runs
    url: string;
}

// MobileAttachments shows a task's images as thumbnails and its other files as a list,
// and adds images from the camera or the library. A task that isn't saved yet holds
// the uploaded files until it is (see MobileTaskEditor).
export function MobileAttachments(props: {
    // taskId is 0 for a task that isn't saved yet
    taskId: number;
    value: Attachment[];
    onChange: (value: Attachment[]) => void;
}) {
    const picker = useRef<HTMLInputElement>(null);
    const preview = useImagePreview();
    const [uploading, setUploading] = useState<Uploading[]>([]);
    const valueRef = useSyncedRef(props.value);

    const add = useSnackAction(async (input: { files: File[] }) => {
        const pending = input.files.map((file, index) => ({
            key: Date.now() + "-" + index,
            url: URL.createObjectURL(file),
        }));

        setUploading(list => list.concat(pending));

        try {
            const added: Attachment[] = [];

            // one at a time: each upload is a round trip to the bucket and back
            for (const file of input.files) {
                const stored = await api.files.upload({ file: file });
                if (props.taskId) await api.tasks.setFile({ task: props.taskId, file: stored.id, archived: false });

                added.push({
                    id: stored.id,
                    name: stored.name,
                    contentType: stored.contentType,
                    size: stored.size,
                    uploadedAt: stored.uploadedAt,
                });
            }

            props.onChange(valueRef.current.concat(added));
        } finally {
            pending.forEach(item => URL.revokeObjectURL(item.url));
            setUploading(list => list.filter(item => !pending.includes(item)));
        }
    }, { success: "Added" });

    const images = props.value.filter(file => file.contentType.startsWith("image/"));
    const others = props.value.filter(file => !file.contentType.startsWith("image/"));

    return (
        <Box sx={{ py: 1 }}>
            {(images.length > 0 || uploading.length > 0) && (
                <Box onClick={preview.onClick} sx={{
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.5, px: `${gutter}px`,
                }}>
                    {images.map(file => (
                        <Box key={file.id} component="img" alt={file.name}
                             src={api.files.downloadUrl({ id: file.id, inline: true })}
                             sx={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 1, cursor: "zoom-in" }} />
                    ))}
                    {uploading.map(item => (
                        <Box key={item.key} sx={{ position: "relative" }}>
                            <Box component="img" alt="" src={item.url}
                                 sx={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 1, opacity: 0.5 }} />
                            <CircularProgress size={24} sx={{ position: "absolute", top: "50%", left: "50%", mt: -1.5, ml: -1.5 }} />
                        </Box>
                    ))}
                </Box>
            )}
            {preview.element}

            {others.length > 0 && (
                <List dense disablePadding>
                    {others.map(file => (
                        <ListItemButton key={file.id} component="a" target="_blank" rel="noopener noreferrer"
                                        href={api.files.downloadUrl({ id: file.id })}>
                            <ListItemIcon sx={{ minWidth: 32 }}><AttachFile fontSize="small" /></ListItemIcon>
                            <ListItemText primary={file.name} />
                        </ListItemButton>
                    ))}
                </List>
            )}

            <Box sx={{ px: `${gutter}px`, pt: 0.5 }}>
                <Button size="small" startIcon={<AddPhotoAlternate />} disabled={add.loading}
                        onClick={() => picker.current?.click()}>
                    Add image
                </Button>

                {/* no capture attribute, so the phone offers the camera and the library */}
                <input ref={picker} type="file" accept="image/*" multiple style={{ display: "none" }}
                       onChange={e => {
                           const files = Array.from(e.target.files || []);
                           e.target.value = "";
                           if (files.length > 0) add.callback({ files: files });
                       }} />
            </Box>
        </Box>
    );
}
